import React, { useState, useEffect, useContext } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { roleConfig } from '../config/roleConfig';
import axios from 'axios';

/**
 * PropertyOwnerGuard - Ensures user can only access properties they own
 * Admins bypass this check
 */
const PropertyOwnerGuard = ({ children }) => {
  const { user } = useContext(AuthContext);
  const { propertyId } = useParams();
  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAccess = async () => {
      // Reset state
      setLoading(true);
      setError(null);

      // Staff roles with bypass bypass ownership check (admin, ops, maintenance, service)
      if (roleConfig[user?.role]?.canBypassOwnershipCheck) {
        setHasAccess(true);
        setLoading(false);
        return;
      }

      // No property ID means list view - allow
      if (!propertyId) {
        setHasAccess(true);
        setLoading(false);
        return;
      }

      try {
        // Try to fetch the property - backend will validate ownership
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/properties/${propertyId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        // If we get here, user has access
        setHasAccess(true);
      } catch (error) {
        if (error.response?.status === 403) {
          setHasAccess(false);
        } else if (error.response?.status === 404) {
          setError('العقار غير موجود');
          setHasAccess(false);
        } else {
          console.error('Error checking property access:', error);
          setError('حدث خطأ في التحقق من الصلاحيات');
          setHasAccess(false);
        }
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      checkAccess();
    }
  }, [propertyId, user]);

  // Loading state
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        direction: 'rtl'
      }}>
        <div>جاري التحقق من صلاحية الوصول للعقار...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        direction: 'rtl'
      }}>
        <h2>خطأ</h2>
        <p>{error}</p>
        <button onClick={() => window.history.back()}>
          العودة
        </button>
      </div>
    );
  }

  // No access — redirect to role's default route to avoid loops
  if (!hasAccess) {
    const fallback = roleConfig[user?.role]?.defaultRoute || '/dashboard';
    return <Navigate to={fallback} replace />;
  }

  // Has access
  return children;
};

export default PropertyOwnerGuard;